import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "src/config/apiBase";
import { decryptToken } from "../utils/getToken";
import { toast } from "react-toastify";
import { Plus } from "lucide-react";
import CrmWorkflowCanvas from "./CrmWorkflowCanvas";

const DEFAULT_RULE = {
  id: "",
  name: "New rule",
  enabled: true,
  trigger: "lead.created",
  conditions: [{ field: "source", op: "contains", value: "google" }],
  actions: [{ type: "set_reminder", hours: 2 }],
};

const renderRuleEditor = (rule, index, updateRule) => (
  <>
    <div className="crm-workflow__flow-row">
      <span className="crm-workflow__when">WHEN</span>
      <select
        className="crm-insights__select"
        value={rule.trigger}
        onChange={(e) => updateRule(index, { trigger: e.target.value })}
      >
        <option value="lead.created">Lead is created</option>
        <option value="stage.changed">Stage changes</option>
      </select>
      <span className="crm-workflow__arrow">→</span>
      <span className="crm-workflow__when">IF</span>
      <input
        className="crm-platform__input"
        value={rule.conditions?.[0]?.value || ""}
        onChange={(e) =>
          updateRule(index, {
            conditions: [{ field: "source", op: "contains", value: e.target.value }],
          })
        }
        placeholder="source contains…"
      />
    </div>
    <div className="crm-workflow__flow-row">
      <span className="crm-workflow__when">THEN</span>
      <select
        className="crm-insights__select"
        value={rule.actions?.[0]?.type || "set_reminder"}
        onChange={(e) => {
          const type = e.target.value;
          const action =
            type === "set_stage"
              ? { type, stage: "Pending" }
              : type === "star"
                ? { type: "star" }
                : { type: "set_reminder", hours: rule.actions?.[0]?.hours || 24 };
          updateRule(index, { actions: [action] });
        }}
      >
        <option value="set_reminder">Set reminder</option>
        <option value="set_stage">Set stage</option>
        <option value="star">Star lead</option>
      </select>
      {(rule.actions?.[0]?.type || "set_reminder") === "set_reminder" && (
        <input
          type="number"
          className="crm-platform__input crm-workflow__hours"
          value={rule.actions?.[0]?.hours ?? 24}
          onChange={(e) =>
            updateRule(index, {
              actions: [{ type: "set_reminder", hours: Number(e.target.value) || 24 }],
            })
          }
        />
      )}
      {rule.actions?.[0]?.type === "set_stage" && (
        <select
          className="crm-insights__select"
          value={rule.actions?.[0]?.stage || "Pending"}
          onChange={(e) =>
            updateRule(index, { actions: [{ type: "set_stage", stage: e.target.value }] })
          }
        >
          <option value="">Uncontacted</option>
          <option value="Pending">Pending</option>
          <option value="In Progress">In Progress</option>
        </select>
      )}
    </div>
    <div className="crm-platform__stage-row">
      <input
        className="crm-platform__input"
        value={rule.name}
        onChange={(e) => updateRule(index, { name: e.target.value })}
        placeholder="Rule name"
      />
      <label>
        <input
          type="checkbox"
          checked={rule.enabled !== false}
          onChange={(e) => updateRule(index, { enabled: e.target.checked })}
        />
        Active
      </label>
    </div>
  </>
);

const CrmWorkflowRulesPanel = () => {
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";

  useEffect(() => {
    fetch(`${url}/leads/workflow-rules`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => setRules(res?.rules || []))
      .catch(() => setRules([]));
  }, [url, token]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${url}/leads/workflow-rules`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rules }),
      });
      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();
      setRules(data.rules || rules);
      toast.success("Workflow rules saved");
    } catch {
      toast.error("Could not save workflow rules");
    } finally {
      setSaving(false);
    }
  };

  const updateRule = (index, patch) => {
    setRules((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addRule = () => {
    setRules((prev) => [...prev, { ...DEFAULT_RULE, id: `rule_${Date.now()}` }]);
  };

  return (
    <div className="crm-workflow">
      <div className="crm-integrations__head">
        <strong>Workflow builder</strong>
        <button type="button" onClick={addRule}>
          <Plus size={14} /> Add rule
        </button>
      </div>
      <p className="crm-insights__muted">
        Drag rules to set priority. Top rules run first on lead create or stage change.
      </p>

      <CrmWorkflowCanvas
        rules={rules}
        onChange={setRules}
        renderRuleEditor={(rule, index) => renderRuleEditor(rule, index, updateRule)}
      />

      <button type="button" className="crm-platform__save" onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Save workflow rules"}
      </button>
    </div>
  );
};

export default CrmWorkflowRulesPanel;
